import { observer } from 'mobx-react'
import React, { PropsWithChildren, useState } from 'react'
import MapboxMap from 'react-map-gl'

import { imageUrls } from '../../constants/imageConstants'
import {
   MAPBOX_DEFAULT_LAYER,
   MAPBOX_SATELLITE_LAYER
} from '../../constants/MapboxConstants'
import { OLA_MAPS_CUSTOM_ATTRIBUTION } from '../../constants/OlaMapsAutocompleteConstants'
import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapStyleEnum } from '../../types'
import { MapViewStateType } from '../../types/MapsServiceProviderTypes'

import MapboxMapContextProvider from '../MapboxMapContextProvider/MapboxMapContextProvider'

interface StyleProps {
   width: string
   height: string
}

interface MapBoxMapServiceProviderProps extends PropsWithChildren {
   mapStyleType: MapStyleEnum
   isEditMode: boolean
   apiKey: string
   viewState: MapViewStateType
   isSearchProviderOlaMaps: boolean
   markLocationCursor: string

   setMapStyleType: React.Dispatch<React.SetStateAction<MapStyleEnum>>
   setViewState: React.Dispatch<React.SetStateAction<MapViewStateType>>
   setIsEditMode: (value: boolean) => void

   className?: string
   styleProps?: StyleProps
}

const getMapStyleBasedOnMapStyleType = (mapStyleType: MapStyleEnum) =>
   mapStyleType === MapStyleEnum.Default
      ? MAPBOX_DEFAULT_LAYER
      : MAPBOX_SATELLITE_LAYER

const MapBoxMapServiceProvider = (
   props: MapBoxMapServiceProviderProps
): React.ReactElement => {
   const {
      viewState,
      styleProps,
      apiKey,
      isSearchProviderOlaMaps,
      markLocationCursor,
      setMapStyleType,
      setViewState,
      mapStyleType,
      isEditMode,
      setIsEditMode
   } = props

   const { customMapStyles } = useMapsUtilityContext()
   const [isCustomStyleError, setIsCustomStyleError] = useState(false)

   const getInitialMapStyle = () => {
      const customStyle = customMapStyles?.satellite ?? customMapStyles?.default
      if (customStyle) return customStyle

      return getMapStyleBasedOnMapStyleType(mapStyleType)
   }

   const mapStyle = isCustomStyleError
      ? getMapStyleBasedOnMapStyleType(mapStyleType)
      : getInitialMapStyle()

   const isMapStyleLoadingError = (errorEvent: any): boolean => {
      const error = errorEvent.error

      const errorurl = error?.url as string | undefined
      const styleParam = mapStyle?.split('mapbox://styles')

      if (errorurl && styleParam?.[1]) {
         const isStylesError = errorurl.includes(styleParam[1])
         if (isStylesError) return true
      }

      return false
   }

   const handleOnMapStyleLoadError = (e: any) => {
      if (!customMapStyles?.satellite) return

      const style = customMapStyles.satellite
      const isCustomMapStyle = mapStyle === style
      if (!isCustomMapStyle) return

      const isDefaultMapStyle = mapStyleType === MapStyleEnum.Default
      if (isDefaultMapStyle) return

      const isStyleError = isMapStyleLoadingError(e)
      if (!isStyleError) return

      setIsCustomStyleError(true)
   }

   return (
      <MapboxMap
         {...viewState}
         onMove={event => setViewState(event.viewState)}
         style={styleProps}
         mapboxAccessToken={apiKey}
         mapStyle={mapStyle}
         onError={handleOnMapStyleLoadError}
         cursor={isEditMode ? markLocationCursor : undefined}
         customAttribution={
            isSearchProviderOlaMaps ? [OLA_MAPS_CUSTOM_ATTRIBUTION] : undefined
         }
      >
         <MapboxMapContextProvider
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            mapStyle={mapStyleType}
            onChangeMapStyle={setMapStyleType}
            zoomValue={viewState.zoom}
            onChangeZoomValue={zoom => setViewState({ ...viewState, zoom })}
         >
            {props.children}
         </MapboxMapContextProvider>
      </MapboxMap>
   )
}

export default observer(MapBoxMapServiceProvider)
